(() => {
  const TILE_SELECTOR = '[data-sxt-brand-tile]';
  const NEWEST_LIMIT = 250;
  const BRAND_POOL_SIZE = 12;
  const HOUR_MS = 60 * 60 * 1000;
  let newestProductsPromise;
  let activeHour = -1;

  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('en')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const productVendor = (product) => normalize(product.vendor || product.brand);

  const matchesBrand = (product, brand) => {
    const vendor = productVendor(product);
    const wanted = normalize(brand);
    if (!vendor || !wanted) return false;
    if (vendor.includes(wanted) || wanted.includes(vendor)) return true;
    return wanted.split(' ').every((token) => vendor.includes(token));
  };

  const productImage = (product) => {
    const image = product.featured_image
      || product.image
      || product.images?.[0]
      || product.featured_media?.preview_image;

    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  };

  const productTime = (product) => {
    const timestamp = Date.parse(product.published_at || product.created_at || product.updated_at || '');
    if (Number.isFinite(timestamp)) return timestamp;
    const numericId = Number(String(product.id || '').replace(/\D/g, ''));
    return Number.isFinite(numericId) ? numericId : 0;
  };

  const isAvailable = (product) => {
    if (typeof product.available === 'boolean') return product.available;
    if (!Array.isArray(product.variants)) return true;
    return product.variants.some((variant) => variant.available !== false);
  };

  const hash = (value) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      result ^= value.charCodeAt(index);
      result = Math.imul(result, 16777619);
    }
    return result >>> 0;
  };

  const hourlyIndex = (brand, hour, length) => {
    if (length <= 1) return 0;
    let current = hash(`${brand}:${hour}`) % length;
    const previous = hash(`${brand}:${hour - 1}`) % length;
    if (current === previous) current = (current + 1) % length;
    return current;
  };

  const shopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const fetchNewestProducts = () => {
    if (newestProductsPromise) return newestProductsPromise;
    const url = `${shopRoot()}collections/all/products.json?limit=${NEWEST_LIMIT}&sort_by=created-descending`;
    newestProductsPromise = fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Products request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => Array.isArray(payload.products) ? payload.products : [])
      .catch(() => []);
    return newestProductsPromise;
  };

  const fetchBrandFallback = (brand) => {
    const params = new URLSearchParams({
      q: brand,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'last'
    });
    return fetch(`${shopRoot()}search/suggest.json?${params}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => payload?.resources?.results?.products || [])
      .then((products) => products.filter((product) => matchesBrand(product, brand)))
      .catch(() => []);
  };

  const candidatePool = async (brand, newestProducts) => {
    let candidates = newestProducts
      .filter((product) => matchesBrand(product, brand) && isAvailable(product) && productImage(product))
      .sort((left, right) => productTime(right) - productTime(left))
      .slice(0, BRAND_POOL_SIZE);

    if (candidates.length === 0) {
      candidates = (await fetchBrandFallback(brand))
        .filter((product) => isAvailable(product) && productImage(product))
        .sort((left, right) => productTime(right) - productTime(left))
        .slice(0, BRAND_POOL_SIZE);
    }
    return candidates;
  };

  const applyProduct = (tile, product) => {
    const imageUrl = productImage(product);
    if (!imageUrl) return;

    const preload = new Image();
    preload.onload = () => {
      tile.style.setProperty('--sxt-brand-product-image', `url("${imageUrl.replace(/"/g, '\\"')}")`);
      tile.dataset.sxtBrandProduct = product.handle || product.id || '';
      tile.dataset.sxtRotationHour = String(activeHour);
      const brand = tile.dataset.sxtBrand || '';
      const productTitle = product.title || '';
      tile.setAttribute('aria-label', productTitle ? `${brand} — ${productTitle}` : brand);
      tile.setAttribute('title', productTitle ? `${brand} — ${productTitle}` : brand);
    };
    preload.src = imageUrl;
  };

  const rotateBrands = async () => {
    const tiles = Array.from(document.querySelectorAll(TILE_SELECTOR));
    if (tiles.length === 0) return;

    const hour = Math.floor(Date.now() / HOUR_MS);
    if (hour === activeHour) return;
    if (activeHour >= 0) newestProductsPromise = undefined;
    activeHour = hour;

    const newestProducts = await fetchNewestProducts();
    await Promise.all(tiles.map(async (tile) => {
      const brand = tile.dataset.sxtBrand || '';
      const candidates = await candidatePool(brand, newestProducts);
      if (candidates.length === 0) return;
      const selected = candidates[hourlyIndex(brand, hour, candidates.length)];
      applyProduct(tile, selected);
    }));
  };

  const scheduleNextHour = () => {
    const delay = HOUR_MS - (Date.now() % HOUR_MS) + 1500;
    window.setTimeout(async () => {
      await rotateBrands();
      scheduleNextHour();
    }, delay);
  };

  const initialize = () => {
    rotateBrands();
    scheduleNextHour();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
